import { ActionButton, ActionButtonType, Drawer } from "@octopusdeploy/design-system-components";
import { useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import type { LiveStatusSummaryResource } from "@octopusdeploy/octopus-server-client";
import { HostingEnvironment } from "@octopusdeploy/octopus-server-client";
import moment from "moment";
import { useState } from "react";
import { Loading } from "~/components/Loading/Loading";
import { useOctopusFeatureToggle } from "~/hooks/useOctopusFeatureToggle";
import { Timeline } from "./Timeline/Timeline";
interface DeploymentTimelineDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    deploymentName: string;
    environmentId: string;
    projectId: string;
    tenantId?: string;
    spaceId: string;
    referringReleaseId: string;
}
const pageSize = 30;
// Inner component that only executes when the drawer is open
const DeploymentTimelineDrawerInner = (props: Omit<DeploymentTimelineDrawerProps, "isOpen" | "onClose">) => {
    const [currentPage, setCurrentPage] = useState(0);
    const [refreshLiveStatus, setRefreshLiveStatus] = useState(true);
    const { result: timelineResponse, isLoading } = useInlineStatusQuery((repository) => {
        return repository.Deployments.getDeploymentTimeline(props.projectId, props.environmentId, props.tenantId, 0, (currentPage + 1) * pageSize);
    }, [props.projectId, props.environmentId, props.tenantId, currentPage], "Timeline Data Query", {
        refetchIntervalInMs: 3000, // Always refresh since this component only renders when drawer is open
    });
    const isGrpcHaEnabled = useOctopusFeatureToggle("grpc-high-availability", false);
    const { result: liveStatus, isLoading: liveStatusLoading } = useInlineStatusQuery(async (repo): Promise<LiveStatusSummaryResource> => {
        const isSelfHosted = (await repo.Licenses.getCurrentStatus()).HostingEnvironment === HostingEnvironment.SelfHosted;
        const isRunningInHACluster = isSelfHosted && (await repo.OctopusServerNodes.all()).length > 1;
        const disabledBecauseHaIsNotSupported = isRunningInHACluster && !isGrpcHaEnabled;
        if (!disabledBecauseHaIsNotSupported) {
            const response = await repo.KubernetesLiveStatus.getLiveStatus(props.projectId, props.environmentId, props.tenantId, true);
            setRefreshLiveStatus(response.Summary.Status !== "NotSupported");
            return response.Summary;
        }
        else {
            // When Live Status is not enabled, hardcode a "NotSupported" response
            setRefreshLiveStatus(false);
            return {
                Status: "NotSupported",
                HealthStatus: "NotSupported",
                SyncStatus: "NotApplicable",
                LastUpdated: moment(),
            };
        }
    }, [isGrpcHaEnabled, props.projectId, props.environmentId, props.tenantId], "Deployment Drawer Live Status Rollup", {
        refetchIntervalInMs: refreshLiveStatus ? 3000 : undefined, // Always refresh when supported since drawer is open
    });
    const loadMore = () => {
        setCurrentPage((prev) => prev + 1);
    };
    const items = timelineResponse?.TimelineItems.Items ?? [];
    const hasMore = (timelineResponse?.TimelineItems?.TotalResults ?? 0) > (timelineResponse?.TimelineItems?.Items?.length ?? 0);
    return (<>
            {(isLoading || liveStatusLoading) && <Loading busy={true}/>}
            <Timeline {...props} liveStatusSummary={liveStatus} timelineData={items} loadMore={loadMore} hasMore={hasMore}/>
        </>);
};
export const DeploymentTimelineDrawer = (props: DeploymentTimelineDrawerProps) => {
    // Lightweight query just for environment and tenant name - only fetches once
    const { result: environment } = useInlineStatusQuery((repository) => repository.Environments.getByIdV2({ spaceId: props.spaceId, id: props.environmentId }), [props.spaceId, props.environmentId], "Environment Name Query");
    const { result: tenant } = useInlineStatusQuery(async (repository) => (props.tenantId ? await repository.Tenants.get(props.tenantId) : null), [props.tenantId], "Tenant Name Query");
    const environmentName = environment ? environment.Name : "Unknown";
    const tenantName = tenant?.Name;
    return (<Drawer isOpen={props.isOpen} onClose={() => {
            return;
        }} title="Deployment Timeline" subtitle={`${props.deploymentName} deployments to ${environmentName}${tenantName ? ` for ${tenantName}` : ""}`} variant="customActions" layout="basic" customActions={[<ActionButton onClick={props.onClose} type={ActionButtonType.Secondary} label="Close"/>]} width="narrow">
            <DeploymentTimelineDrawerInner deploymentName={props.deploymentName} environmentId={props.environmentId} projectId={props.projectId} tenantId={props.tenantId} spaceId={props.spaceId} referringReleaseId={props.referringReleaseId}/>
        </Drawer>);
};
